import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBuilding, FaClipboardList, FaExternalLinkAlt, FaIndustry } from 'react-icons/fa';
import {
  fetchMyPublishedInfrastructureListings,
  fetchSellerListingInventoryDetail,
} from '../../services/infrastructureLeasingListing.service';
import { aggregateInventoryKpisFromMyPublishedItems, filterAllocationsExpiringSoon } from '../../utils/infrastructureSellerInventoryAggregate';
import { getOwnerInventorySnapshotLine, getSearchCardAvailabilityBadge } from '../../utils/infrastructureListingInventoryAvailabilityUi';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_search_listings.scss';
import '../../assets/styles/infrastructure_seller_inventory.scss';

const listingTab = (item) => (item.listingType === 'machinery' ? 'equipment' : 'real-estate');

async function poolMap(arr, chunkSize, mapper) {
  const out = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    const part = await Promise.all(chunk.map(mapper));
    out.push(...part);
  }
  return out;
}

export default function InfrastructureLeasingSellerInventoryOverviewPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiryRows, setExpiryRows] = useState([]);
  const [loadingExpiry, setLoadingExpiry] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMyPublishedInfrastructureListings({ page: 1, pageSize: 100 });
      if (res?.success && res.data && Array.isArray(res.data.items)) {
        setItems(res.data.items);
      } else {
        setItems([]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not load listings.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = useMemo(() => aggregateInventoryKpisFromMyPublishedItems(items), [items]);

  useEffect(() => {
    const tracked = items.filter((it) => it?.inventoryAvailability?.inventoryManaged);
    if (tracked.length === 0) {
      setExpiryRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingExpiry(true);
      try {
        const results = await poolMap(tracked, 6, async (it) => {
          try {
            const r = await fetchSellerListingInventoryDetail(it.id);
            if (!r?.success || !r.data) return [];
            const alloc = Array.isArray(r.data.allocations) ? r.data.allocations : [];
            return filterAllocationsExpiringSoon(alloc, { withinDays: 90 }).map((a) => ({
              listingId: it.id,
              listingTitle: it.title,
              enquiryId: a.infrastructure_listing_enquiry_id,
              leaseEnd: String(a.lease_end_date || '').slice(0, 10),
              status: a.allocation_status,
              contact: [a.contact_name, a.contact_company].filter(Boolean).join(' · ') || '—',
            }));
          } catch {
            return [];
          }
        });
        if (!cancelled) {
          setExpiryRows(results.flat());
        }
      } finally {
        if (!cancelled) setLoadingExpiry(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  return (
    <div className="container-fluid ile-seller-inv ile-seller-inv--overview py-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard/seller">Seller dashboard</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/infrastructure-leasing/my-properties">My properties / assets</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Inventory operations
          </li>
        </ol>
      </nav>

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h1 className="h3 mb-1 d-flex align-items-center gap-2">
            <FaClipboardList aria-hidden />
            Inventory operations
          </h1>
          <p className="text-muted mb-0 small">
            Portfolio view of tracked stock, availability, and upcoming lease ends. Open a listing for full configuration, blocks, and renewals.
          </p>
        </div>
        <Link to="/infrastructure-leasing/my-properties" className="btn btn-outline-secondary btn-sm">
          ← My properties
        </Link>
      </div>

      {loading ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <>
          <div className="row g-3 mb-4">
            <div className="col-6 col-lg-3">
              <div className="ile-seller-inv-kpi">
                <div className="ile-seller-inv-kpi__label">Tracked listings</div>
                <div className="ile-seller-inv-kpi__value">{kpis.trackedListings}</div>
                <div className="ile-seller-inv-kpi__meta">of {kpis.listingsTotal} published</div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="ile-seller-inv-kpi">
                <div className="ile-seller-inv-kpi__label">Total units (summed)</div>
                <div className="ile-seller-inv-kpi__value">{kpis.sumTotalUnits.toLocaleString('en-IN')}</div>
                <div className="ile-seller-inv-kpi__meta">
                  Available {kpis.sumAvailUnits.toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="ile-seller-inv-kpi">
                <div className="ile-seller-inv-kpi__label">Area (summed sq ft)</div>
                <div className="ile-seller-inv-kpi__value">{kpis.sumTotalArea.toLocaleString('en-IN')}</div>
                <div className="ile-seller-inv-kpi__meta">
                  Avail {kpis.sumAvailArea.toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="ile-seller-inv-kpi">
                <div className="ile-seller-inv-kpi__label">Vacancy (units)</div>
                <div className="ile-seller-inv-kpi__value">
                  {kpis.vacancyPctUnits != null ? `${kpis.vacancyPctUnits}%` : '—'}
                </div>
                <div className="ile-seller-inv-kpi__meta">
                  Occ {kpis.occupancyPctUnits != null ? `${kpis.occupancyPctUnits}%` : '—'}
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom">
              <strong className="small text-uppercase text-muted">Allocations ending within 90 days</strong>
              {loadingExpiry && <span className="ms-2 text-muted small">loading…</span>}
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0 ile-seller-inv-table">
                <thead className="table-light">
                  <tr>
                    <th>Listing</th>
                    <th>Enquiry</th>
                    <th>Counterparty</th>
                    <th>Status</th>
                    <th>Lease end</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {expiryRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-muted small py-4 px-3">
                        {kpis.trackedListings === 0
                          ? 'No listings use tracked inventory yet. Enable inventory when publishing or add lines from a listing hub.'
                          : 'No reserved or confirmed allocations ending in the next 90 days.'}
                      </td>
                    </tr>
                  ) : (
                    expiryRows.map((row) => (
                      <tr key={`${row.listingId}-${row.enquiryId}-${row.leaseEnd}`}>
                        <td className="fw-medium">{row.listingTitle}</td>
                        <td>#{row.enquiryId}</td>
                        <td className="small">{row.contact}</td>
                        <td>
                          <span className="badge rounded-pill text-bg-secondary text-uppercase">{row.status}</span>
                        </td>
                        <td>{row.leaseEnd}</td>
                        <td className="text-end text-nowrap">
                          <Link
                            to={`/infrastructure-leasing/my-properties/${row.listingId}/inventory?tab=renewals`}
                            className="btn btn-sm btn-dark"
                          >
                            Renewals
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
              <strong className="small text-uppercase text-muted">All published listings</strong>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0 ile-seller-inv-table">
                <thead className="table-light">
                  <tr>
                    <th>Asset</th>
                    <th>Type</th>
                    <th>Availability</th>
                    <th>Inventory snapshot</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-muted py-4 px-3">
                        No published listings.
                      </td>
                    </tr>
                  ) : (
                    items.map((it) => {
                      const badge = getSearchCardAvailabilityBadge(it);
                      const snap = getOwnerInventorySnapshotLine(it.inventoryAvailability);
                      const isMachinery = it.listingType === 'machinery';
                      return (
                        <tr key={it.id}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {isMachinery ? <FaIndustry className="text-secondary" /> : <FaBuilding className="text-secondary" />}
                              <span className="fw-medium">{it.title}</span>
                            </div>
                          </td>
                          <td className="small text-muted">{isMachinery ? 'Machinery' : 'Property'}</td>
                          <td>
                            {badge ? (
                              <span className={`ile-search-badge ${badge.className}`}>{badge.label}</span>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td className="small">{snap || <span className="text-muted">Not tracked</span>}</td>
                          <td className="text-end text-nowrap">
                            <Link
                              to={`/infrastructure-leasing/my-properties/${it.id}/inventory`}
                              className="btn btn-sm btn-dark me-1"
                            >
                              Inventory hub
                            </Link>
                            <Link
                              to={`/infrastructure-leasing/listing/${it.id}?tab=${listingTab(it)}`}
                              className="btn btn-sm btn-outline-secondary"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Public <FaExternalLinkAlt className="ms-1" size={11} />
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
